/**
 * Responsive 2-column slot picker. Each date is its own card; each time
 * inside the card is a pill button. Booked slots are rendered as
 * disabled with a "Booked" badge so the user can clearly see what's
 * already taken.
 *
 * Mirrors `microlab/.../DemoSlotPicker.tsx` 1:1 — same visual language so
 * the experience reads the same on web and mobile.
 */

import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import ButtonBase from "@mui/material/ButtonBase";
import { useTheme } from "@mui/material/styles";

import { COPY, DEMO_MAX_SLOT_SELECTIONS } from "./demoRequestConstants";
import {
  formatDateLabel,
  formatSlotLabel,
  formatTimeLabel,
  groupSlotsByDate,
  slotKey,
} from "./demoBookingUtils";

export default function DemoSlotPicker({
  availableSlots,
  selectedSlots,
  bookedSlots,
  onChange,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const datesWithTimes = groupSlotsByDate(availableSlots);
  const selectedKeys = new Set(selectedSlots.map(slotKey));
  const availableKeys = new Set(availableSlots.map(slotKey));
  const bookedKeys = new Set((bookedSlots || []).map(slotKey));

  const toggleSlot = (date, time) => {
    const key = `${date}_${time}`;
    if (!availableKeys.has(key)) return;
    if (bookedKeys.has(key)) return;

    if (selectedKeys.has(key)) {
      onChange(selectedSlots.filter((s) => slotKey(s) !== key));
      return;
    }
    const next = { date, time };
    if (selectedSlots.length >= DEMO_MAX_SLOT_SELECTIONS) {
      onChange([next]);
      return;
    }
    onChange([...selectedSlots, next]);
  };

  return (
    <Box>
      {selectedSlots.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="caption"
            sx={{ fontWeight: 700, color: "text.secondary", letterSpacing: 0.4 }}
          >
            {COPY.selectedSlot.toUpperCase()}
          </Typography>
          <Stack spacing={1} sx={{ mt: 0.5 }}>
            {selectedSlots.map((s) => (
              <ButtonBase
                key={slotKey(s)}
                onClick={() => toggleSlot(s.date, s.time)}
                sx={{
                  justifyContent: "space-between",
                  width: "100%",
                  px: 2,
                  py: 1.25,
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.primary.main}`,
                  bgcolor: `${theme.palette.primary.main}22`,
                  textAlign: "left",
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: 14,
                    color: "text.primary",
                    flex: 1,
                  }}
                >
                  {formatSlotLabel(s)}
                </Typography>
                <Typography sx={{ color: "primary.main", fontSize: 18, ml: 1 }}>
                  ×
                </Typography>
              </ButtonBase>
            ))}
          </Stack>
        </Box>
      )}

      <Stack spacing={1.5}>
        {datesWithTimes.map(({ date, times }) => (
          <Box
            key={date}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2.5,
              p: 2,
              bgcolor: isDark ? "background.paper" : "background.default",
            }}
          >
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: 15,
                color: "text.primary",
                mb: 1.25,
              }}
            >
              {formatDateLabel(date)}
            </Typography>
            <Grid container spacing={1.25}>
              {times.map((time) => {
                const key = `${date}_${time}`;
                const isSelected = selectedKeys.has(key);
                const isBooked = bookedKeys.has(key);

                const borderColor = isSelected
                  ? theme.palette.primary.main
                  : theme.palette.divider;
                const bgColor = isBooked
                  ? isDark
                    ? "#1F1F23"
                    : "#ECECEF"
                  : isSelected
                    ? `${theme.palette.primary.main}28`
                    : isDark
                      ? "#2A2A2E"
                      : "#F4F6FA";
                const labelColor = isBooked
                  ? theme.palette.text.secondary
                  : isSelected
                    ? theme.palette.primary.main
                    : theme.palette.text.primary;

                return (
                  <Grid
                    key={time}
                    item
                    xs={times.length > 1 ? 6 : 12}
                    sm={times.length > 1 ? 4 : 12}
                    md={times.length > 1 ? 3 : 12}
                  >
                    <ButtonBase
                      onClick={() => toggleSlot(date, time)}
                      disabled={isBooked}
                      sx={{
                        width: "100%",
                        py: 1.5,
                        borderRadius: 2,
                        border: `1px solid ${borderColor}`,
                        bgcolor: bgColor,
                        opacity: isBooked ? 0.65 : 1,
                        flexDirection: "column",
                        gap: 0.25,
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: 14,
                          color: labelColor,
                          textDecoration: isBooked ? "line-through" : "none",
                        }}
                      >
                        {formatTimeLabel(time)}
                      </Typography>
                      {isBooked && (
                        <Typography
                          sx={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: "text.secondary",
                            letterSpacing: 0.5,
                            textTransform: "uppercase",
                          }}
                        >
                          {COPY.slotBooked}
                        </Typography>
                      )}
                    </ButtonBase>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

DemoSlotPicker.propTypes = {
  availableSlots: PropTypes.array.isRequired,
  selectedSlots: PropTypes.array.isRequired,
  bookedSlots: PropTypes.array,
  onChange: PropTypes.func.isRequired,
};

DemoSlotPicker.defaultProps = {
  bookedSlots: [],
};
